/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { FlightLegInfo } from '../../../../shared/entities/flightLeg';
import MissionPlanDetails from '../../../../shared/entities/missionPlanDetails';
import { selectFlightLegServiceAlerts } from '../../../../store';
import { setSelectedOverlaidFlightLegId } from '../../../../store/flightLegs/actions';
import { getServiceAlerts } from '../../../../store/flightLegServiceAlerts/actions';
import { formatPOExportMissoinCreatedDate } from '../../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../utils/StringUtils';
import appTheme from '../../../common/theme/appTheme.json';
import { ContourDisplayEnum } from '../PerformanceOutlookExportModal';
import PerformanceOutlookFlightEventsList from '../PerformanceOutlookFlightEventsList';
import PerformanceOutlookLegInfo from '../PerformanceOutlookLegInfo';
import PerformanceOutlookHorizontalTimeline from '../TimelineChart/PerformanceOutlookHorizontalTimeline';
import { useHeatMapsEnabled } from './performanceOutlookExportHelperHooks';
import PerformanceOutlookExportLegend from './PerformanceOutlookExportLegend';
import PerformanceOutlookExportMapContainer from './PerformanceOutlookExportMapContainer';
import PerformanceOutlookExportPrintModeHeader from './PerformanceOutlookExportPrintModeHeader';

export const PO_EXPORT_STANDARD_PRINT_MAP_HEIGHT = '600px';
export const PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH = '1100px';
const EXPORT_CHART_WIDTH = 980;

export const useStyles = makeStyles({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '@media print': {
      display: 'block',
    },
  },
  flightLegHeaderContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    height: '86px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    '@media print': {
      height: '0px',
    },
  },
  mapContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    height: PO_EXPORT_STANDARD_PRINT_MAP_HEIGHT,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    '@media print': {
      height: '550px',
    },
  },

  horizontalineTimelineContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    height: '100px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    marginTop: '33px',
    '@media print': {
      height: '80px',
    },
  },
  legendContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    height: '21px',
    display: 'flex',
    justifyContent: 'flex-end',
    position: 'relative',
  },
  missionContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    height: '21px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  eventListContainer: {
    width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
    position: 'relative',
    display: 'flex',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    '@media print': {
      display: 'block',
      pageBreakBefore: 'always !important',
      pageBreakAfter: 'always !important',
      pageBreakInside: 'avoid',
    },
  },
});

export interface PerfomanceOutlookStandardPrintProps {
  flightLeg: FlightLegInfo;
  missionPlan: MissionPlanDetails;
  userId: string;
  totalFlightLegs: number;
  contourDisplayType: ContourDisplayEnum;
}

const PerfomanceOutlookStandardPrint = (props: PerfomanceOutlookStandardPrintProps) => {
  const { flightLeg, missionPlan, userId, totalFlightLegs, contourDisplayType } = props;

  const classes = useStyles();
  const dispatch = useDispatch();
  const serviceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);
  const [mapId] = useState(`exportMap-${flightLeg.id}`);

  useEffect(() => {
    if (missionPlan.id && userId && flightLeg.id) {
      dispatch(getServiceAlerts(missionPlan.id, flightLeg.id, userId));
      dispatch(setSelectedOverlaidFlightLegId(mapId)(flightLeg.id));
    }
  }, [dispatch, missionPlan.id, userId, flightLeg.id, mapId]);

  useHeatMapsEnabled(contourDisplayType, mapId);

  if (!serviceAlerts) {
    return null;
  }
  return (
    <Box className={classes.container}>
      {/* flight leg header */}
      <PerformanceOutlookExportPrintModeHeader flightLeg={flightLeg} totalFlightLegs={totalFlightLegs} />
      {/* map */}
      <Box className={classes.mapContainer}>
        <PerformanceOutlookExportMapContainer
          flightLeg={flightLeg}
          missionPlan={missionPlan}
          serviceAlerts={serviceAlerts[flightLeg.id]}
          userId={userId}
          mapId={mapId}
          topPosition="0px"
        />
      </Box>
      {/* flight info */}
      <Box>
        <PerformanceOutlookLegInfo
          missionPlan={missionPlan}
          serviceAlerts={serviceAlerts}
          flightLegId={flightLeg.id}
          contourDisplayType={contourDisplayType}
          serviceTiersSlaChartEnabled={false}
          sx={{
            padding: '24px',
            width: PO_EXPORT_STANDARD_PRINT_DEFAULT_WIDTH,
            '@media print': {
              padding: 0,
              color: 'black',
            },
          }}
        />
      </Box>

      {/* horizontal timeline */}
      <Box className={classes.horizontalineTimelineContainer}>
        <PerformanceOutlookHorizontalTimeline
          key={flightLeg.id}
          chartWidth={EXPORT_CHART_WIDTH}
          chartTopPosition={0}
          flightLegId={flightLeg.id}
          mapId={mapId}
          disableTimelineHover
        />
      </Box>
      {/* legend */}
      {/* mission create date */}
      <Box className={classes.missionContainer}>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              fontFamily: appTheme.viasatFontFamilyRegular,
              color: appTheme.primaryTextColor,
              fontSize: '12px',
              lineHeight: '21px',
              fontWeight: 500,
              width: '30%',
              '@media print': {
                color: 'black',
              },
            }}
          >
            Mission created:
            {missionPlan.creationDate
              ? ` ${formatPOExportMissoinCreatedDate(missionPlan.creationDate)}Z`
              : EMPTY_FIELD_STRING_PLACEHOLDER}
          </Box>

          <Box sx={{ position: 'relative', width: '70%' }}>
            <PerformanceOutlookExportLegend flightLegId={flightLeg.id} />
          </Box>
        </Box>
      </Box>
      {/*flight event list */}
      <Box className={classes.eventListContainer}>
        <PerformanceOutlookFlightEventsList key={flightLeg.id} isPOExportPage={true} flightLegId={flightLeg.id} />
      </Box>
    </Box>
  );
};

export default PerfomanceOutlookStandardPrint;

export const StyledFlightLegHead = styled.h1`
  font-family: ${appTheme.viasatFontFamilyRegular};
  color: ${appTheme.primaryTextColor};
  font-size: 25px;
  font-weight: 500;
  margin-top: 70px;
  @media print {
    color: black;
    margin-top: 0px;
  }
`;
